import React, {memo, useEffect, useState} from "react";
import {VariationsWrapper} from "@montana/pages/product-page/variation-pickers/variations-wrapper";
import {useProduct} from "@montana/containers/product";
import {SmallerThanTabletLandscape, TabletLandscapeAndLarger} from "@montana/shared/responsive";
import {DimensionPicker, MeasurementPicker} from "./dimension-picker";
import {SvgIcon} from "@montana/shared/svg-icon";
import {getDimensionMeta, getDimensionTranslationValue, isDimensionConditionMet, isOptionConditionMet as isOptionConditionMetHelper} from "@montana/helpers/product.helper";
import {useTranslations} from "@montana/containers/translations";
import {usePDPContext} from "../product-page.context";
import {IVariation} from "./variations-wrapper/variation";
import {IOption, IProductDimension} from '@montana/models/generated/cms.interface';
import {LazyImage} from '@montana/shared/lazy-image';

export interface IVariationPickersProps {
    onShowGallery?: () => void;
    onZoom?: () => void;
}

// Array determining the sort order for known dimensions.
export const preferredDimensionOrder = ['upholstery type', 'upholstery', 'colour', 'top plate', 'base type', 'width', 'leg finish', 'wire', 'shell finish', 'frame finish'];

export const VariationPickers = memo(({onShowGallery, onZoom}: IVariationPickersProps) => {
    const {dimensions, measurements, config, gallery, showDepthAsHeight} = useProduct();
    const {activeVariationPicker, setActiveVariationPicker} = usePDPContext();
    const translate = useTranslations();
    const [showConfiguration, setShowConfiguration] = useState<boolean>(false);

    useEffect(() => {
        if (!!activeVariationPicker) {
            setShowConfiguration(true);
        } else {
            setShowConfiguration(false);
        }
    }, [activeVariationPicker])

    useEffect(() => {
        if (!showConfiguration) {
            setActiveVariationPicker('');
        }
    }, [showConfiguration])

    if (!dimensions?.length) {
        return null;
    }

    const getSelectedDimensionValue = (dimension: IProductDimension): string => {
        if (!config || !config.dimensions) {
            return '';
        }
        const dimensionOption = config.dimensions?.filter(dimOption => dimOption.dimensionName === dimension.dimensionName)

        return dimensionOption?.[0]?.displayValue ?? '';
    }

    function isOptionConditionMet(dimension: IProductDimension): IProductDimension {
        if (!dimension.options) {
            return dimension;
        }
        const curatedOptions: {[k: string]: IOption[]} = {};
        for (const [key, option] of Object.entries(dimension.options)) {
            curatedOptions[key] = [];
            option?.forEach(optionValue => {
                if (isOptionConditionMetHelper(optionValue, config?.dimensions, config?.measurement)) {
                    curatedOptions[key].push(optionValue);
                }
            });
        }
        return {...dimension, options: curatedOptions};
    }

    const variationItems: IVariation[] = [
        ...dimensions
            .slice()
            .sort((a, b) => preferredDimensionOrder.indexOf(a.dimensionName?.toLowerCase() || '') - preferredDimensionOrder.indexOf(b.dimensionName?.toLowerCase() || ''))
            .filter(dimension => isDimensionConditionMet(dimension, config?.dimensions))
            .map(dimension => isOptionConditionMet(dimension))
            .map(dimension => ({
                dimensionName: dimension.dimensionName || '',
                buttonText: dimension.translationDimensionName,
                buttonIcon: !!dimension.icon && !!dimension.icon.url ?
                    <LazyImage className="variations-wrapper__icon" src={dimension.icon?.url} immediate/> : '',
                component: <DimensionPicker key={dimension.dimensionName} dimension={dimension}/>,
                selectedValue: getSelectedDimensionValue(dimension)
            })),
        ...(measurements && measurements.length > 1 ? [{
            dimensionName: 'Measurements',
            buttonText: showDepthAsHeight ? getDimensionMeta(translate, 'Height').name : getDimensionMeta(translate, 'Measurements').name,
            buttonIcon: <SvgIcon className="variations-wrapper__icon"
                                 iconName={getDimensionMeta(translate, 'Measurements').icon}/>,
            component: <MeasurementPicker
                headline={showDepthAsHeight ? getDimensionMeta(translate, 'Height').name : getDimensionMeta(translate, 'Measurements').name}/>,
            selectedValue: getDimensionTranslationValue(translate, config?.measurement)
        }] : []),
        ...(!!gallery?.length ? [{
            dimensionName: 'Gallery',
            buttonText: getDimensionMeta(translate, 'Gallery').name,
            buttonIcon: <SvgIcon className="variations-wrapper__icon"
                                 iconName={getDimensionMeta(translate, 'Gallery').icon}/>,
            clickHandler: onShowGallery,
            spacing: true,
        }] : []),
        {
            dimensionName: 'Zoom',
            buttonText: getDimensionMeta(translate, 'Zoom').name,
            buttonIcon: <SvgIcon className="variations-wrapper__icon"
                                 iconName={getDimensionMeta(translate, 'Zoom').icon}/>,
            clickHandler: onZoom,
            spacing: !gallery?.length,
        },
    ];

    return (
        <section className={`variation-picker ${showConfiguration ? ' variation-picker--active' : ''}`}>
            <SmallerThanTabletLandscape includeSSR>
                <div className="variation-picker__container">
                    <VariationsWrapper variations={variationItems} mobileLayout/>
                </div>
            </SmallerThanTabletLandscape>
            <TabletLandscapeAndLarger>
                <VariationsWrapper variations={variationItems}/>
            </TabletLandscapeAndLarger>
        </section>
    );
});
